import { useEffect, useMemo, useRef, useState } from 'react'
import { createFileRoute, useParams } from '@tanstack/react-router'
import { createClient } from '@connectrpc/connect'
import { create } from '@bufbuild/protobuf'
import { useQuery } from '@connectrpc/connect-query'
import { AlertTriangle, FileQuestion, Loader2 } from 'lucide-react'
import { CortadoService } from '@/gen/cortado/v1/cortado_pb'
import { getInquiryOrgInfo } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { GetInquiryOrgInfoRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import { connectTransport } from '@/lib/connect-transport'
import { bootstrapPortalLink, type PortalLinkBootstrapOutcome } from '@/lib/portal-link-bootstrap'
import { getOrCreatePortalBrowserId } from '@/lib/portal-browser-id'
import { setToken } from '@/stores/token.store'
import { ResendLinkCard } from '@/components/resend-link-card'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

export const Route = createFileRoute('/p/$code')({
  component: PortalLinkBootstrapPage,
})

const portalClient = createClient(CortadoService, connectTransport)

function PortalLinkBootstrapPage() {
  const { code } = useParams({ from: '/p/$code' })
  const resolution = useRef<
    | {
        code: string
        promise: Promise<PortalLinkBootstrapOutcome>
      }
    | undefined
  >(undefined)
  const [outcome, setOutcome] = useState<PortalLinkBootstrapOutcome | 'loading'>('loading')

  useEffect(() => {
    setOutcome('loading')
    let currentResolution = resolution.current
    if (currentResolution?.code !== code) {
      currentResolution = {
        code,
        promise: bootstrapPortalLink(code, {
          getBrowserId: getOrCreatePortalBrowserId,
          resolve: async (shortCode, browserId) => portalClient.resolvePortalLink({ code: shortCode, browserId }),
          storeToken: setToken,
          replaceLocation: (destinationPath) => window.location.replace(destinationPath),
        }),
      }
      resolution.current = currentResolution
    }
    let active = true
    void currentResolution.promise.then((nextOutcome) => {
      if (active) setOutcome(nextOutcome)
    })
    return () => {
      active = false
    }
  }, [code])

  const orgRequest = useMemo(
    () => create(GetInquiryOrgInfoRequestSchema, { shortCode: code }),
    [code],
  )
  const { data: orgInfo } = useQuery(getInquiryOrgInfo, orgRequest, {
    enabled: outcome === 'expired',
    retry: false,
  })

  if (outcome === 'loading' || outcome === 'success') {
    return (
      <main className="flex min-h-svh items-center justify-center bg-background p-8" aria-live="polite">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          <span>Opening your secure portal…</span>
        </div>
      </main>
    )
  }

  if (outcome === 'expired') {
    return (
      <main className="flex min-h-svh items-center justify-center bg-background p-8">
        <div className="w-full max-w-md space-y-8">
          <PortalLinkBrand orgName={orgInfo?.orgName} orgLogoUrl={orgInfo?.orgLogoUrl} />
          <Card className="border-destructive">
            <CardHeader>
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-destructive" />
                <div>
                  <CardTitle className="text-destructive">Link expired</CardTitle>
                  <CardDescription>
                    {orgInfo?.canResend ? 'Request a fresh secure link below' : 'Ask the business for a new link'}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {orgInfo?.canResend
                  ? 'This portal link has expired. We can send a new one to the original recipient.'
                  : 'This portal link has expired. Contact the business that shared it and ask for a new link.'}
              </p>
            </CardContent>
          </Card>
          {orgInfo?.canResend && (
            <ResendLinkCard shortCode={code} recipientEmailHint={orgInfo.recipientEmailHint} />
          )}
        </div>
      </main>
    )
  }

  return (
    <main className="flex min-h-svh items-center justify-center bg-background p-8">
      <div className="w-full max-w-md space-y-8">
        <PortalLinkBrand />
        <Card className="border-amber-300 bg-amber-50/50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <FileQuestion className="h-6 w-6 text-amber-700" />
              <div>
                <CardTitle>Link unavailable</CardTitle>
                <CardDescription>This secure portal link can’t be opened</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Please contact the business that sent this link and ask for a new one.
            </p>
          </CardContent>
        </Card>
      </div>
    </main>
  )
}

function PortalLinkBrand({ orgName, orgLogoUrl }: { orgName?: string; orgLogoUrl?: string }) {
  return (
    <div className="space-y-4 text-center">
      {orgLogoUrl ? (
        <img src={orgLogoUrl} alt={orgName || 'Organization'} className="mx-auto h-20 w-20 rounded-lg object-cover" />
      ) : (
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-muted">
          <FileQuestion className="h-10 w-10 text-muted-foreground" />
        </div>
      )}
      {orgName && <h1 className="display-heading text-3xl font-semibold tracking-tight">{orgName}</h1>}
    </div>
  )
}
